import { isEmpty } from 'lodash'
import { EVENTS } from '~/common/constants'
import { store, observable, action, computed, event } from '~/common/mobx.decorator'
import { markNotified, toUserMessage, wasNotified } from '~/common/helpers/user-message.helper'
import Bowser from 'bowser'

/*
 * How long each kind stays up (ms), following Atlassian's flags and Carbon's
 * toasts: a confirmation can go on its own, a problem stays until it is read and
 * closed. Hovering or focusing a toast pauses the countdown (MUI Snackbar).
 * `null` means it stays until dismissed.
 */
export const TOAST_DURATION = {
	success: 5000,
	info: 6000,
	warning: 8000,
	error: null,
}

// The same sentence twice inside this window is one event reported twice (a screen and the error link, a double click).
export const DUPLICATE_WINDOW_MS = 2500

// What the caller asked for, else the kind's default. `persist` is the older way to say "until dismissed".
const durationFor = (variant, options) => {
	if (options?.duration !== undefined) return options.duration
	if (options?.persist) return null
	return variant in TOAST_DURATION ? TOAST_DURATION[variant] : TOAST_DURATION.info
}

// A React element passed as the message (bold names and the like); shown as given.
const isNode = (value) => Boolean(value && typeof value === 'object' && value.$$typeof)
// `$ERRORS.MAXIMUM_FILE_SIZE` and friends: our own copy, translated where it is shown.
const isI18nKey = (value) => typeof value === 'string' && /^\$[A-Z_]+\.[A-Z0-9_.]+$/.test(value)

@store()
class NotifyStore {
	// Shallow: a message can be a React element, which must reach the screen as it was passed, not as a deep observable copy.
	@observable.shallow messages = []
	@observable doneMessages = {}

	@observable.shallow dialogs = []
	@observable doneDialogs = {}

	@computed
	get waitMessages() {
		return this.messages.filter((message) => !this.doneMessages[message.key])
	}

	@computed
	get waitDialogs() {
		return this.dialogs.filter((dialog) => !this.doneDialogs[dialog.key])
	}

	@computed
	get signal() {
		return window.OneSignal || []
	}

	recent = new Map()

	// True when the same text was shown a moment ago, so this one is a repeat rather than news.
	isRepeat = (message, variant) => {
		if (typeof message !== 'string' || !message) return false
		const id = `${variant}:${message}`
		const now = Date.now()
		const last = this.recent.get(id)
		this.recent.set(id, now)
		if (this.recent.size > 50) this.recent.delete(this.recent.keys().next().value)
		return last !== undefined && now - last < DUPLICATE_WINDOW_MS
	}

	/**
	 * Show a message.
	 *
	 * `message` is the toast's main line (a string, an i18n key, or a node). Options:
	 * - `description`: a second, quieter line
	 * - `action`: `{ label, onClick }` — one button, e.g. "View" or "Retry"
	 * - `actions`: the older list form of the same
	 * - `duration`: ms before it hides; `null` keeps it until dismissed
	 * - `showDialog`: a blocking dialog instead (confirmations, not results)
	 */
	@action
	open = async (message, options = {}) => {
		const variant = options?.variant || 'default'
		if (!options?.showDialog && this.isRepeat(message, variant)) return undefined

		const data = {
			key: `${Date.now()}_${Math.random()}`,
			message,
			options: {
				...options,
				duration: durationFor(variant, options),
			},
		}

		if (options.showDialog) {
			this.dialogs.push(data)

			return data.key
		}

		// if (this.signal.initialized && routerStore.os?.name !== 'iOS' && Notification?.permission === 'granted') {
		// 	return this.signal.push(() => {
		// 		this.signal.sendSelfNotification(options?.title || appStore.name, message, options?.url, options?.icon)
		// 	})
		// }

		if (options?.onlyWebPush) {
			return
		}

		this.messages.push(data)
		return data.key
	}

	@action
	success = async (message, options) => {
		return this.open(message, { ...options, variant: 'success' })
	}

	/**
	 * Report a failure. Pass the error itself rather than `error.message`: it is
	 * turned into a sentence the recruiter can act on (`toUserMessage`), and an
	 * error the API layer already reported is not reported a second time.
	 *
	 * `options.fallback` says what failed when the server's text is not fit to show,
	 * e.g. `notifyStore.error(error, { fallback: 'Couldn’t save the template.' })`.
	 * `options.cause` is the error behind a sentence the screen wrote itself, so the
	 * API layer knows it was reported: `notifyStore.error(text, { cause: error })`.
	 */
	@action
	error = async (messageOrError, options = {}) => {
		if (options?.showDialog) return this.open(messageOrError, { ...options, variant: 'error' })
		// The error this message is about: either passed as the message, or as `cause` next to a sentence the screen wrote itself.
		const cause = options?.cause || (messageOrError && typeof messageOrError === 'object' && !isNode(messageOrError) ? messageOrError : null)
		if (cause) {
			if (wasNotified(cause)) return undefined
			markNotified(cause)
		}
		const message = isNode(messageOrError) || isI18nKey(messageOrError) ? messageOrError : toUserMessage(messageOrError, options?.fallback)
		return this.open(message, { ...options, variant: 'error' })
	}

	@action
	info = async (message, options) => {
		return this.open(message, { ...options, variant: 'info' })
	}

	@action
	warning = async (message, options) => {
		return this.open(message, { ...options, variant: 'warning' })
	}

	@action
	closeMessage = async (key) => {
		this.doneMessages[key] = true
	}

	@action
	closeDialog = async (key) => {
		this.doneDialogs[key] = true
	}

	@event(EVENTS.notifyStore.fireError)
	handleError({ payload }) {
		this.error(payload?.message, payload?.options)
	}

	@action
	initOneSignal = async (config, worker, notificationChannel) => {
		if (!isEmpty(config?.oneSignalAppId)) {
			const browser = Bowser.getParser(window.navigator.userAgent)

			this.signal.push(() => {
				this.signal.init({
					appId: config?.oneSignalAppId,
					autoRegister: true,
					autoResubscribe: true,
					allowLocalhostAsSecureOrigin: true,
					notifyButton: { enable: true },
				})
			})

			setTimeout(() => {
				this.signal.push(() => {
					this.signal
						.sendTags({
							companyId: config?.companyId,
							userType: 'Worker',
							browser: browser.getBrowser(),
							workerId: worker?.id,
							workerEmail: worker?.email,
						})
						.then((tagsSent) => {
							console.log('**************')
							console.log('tagsSent: ', tagsSent)
						})
				})

				this.signal.push(() => {
					this.signal.setExternalUserId(notificationChannel)
				})

				this.signal.getExternalUserId().then((externalUserId) => {
					console.log('**************')
					console.log('externalUserId: ', externalUserId)
				})
			}, 3000)
		}
	}
}

export const notifyStore = new NotifyStore()
